/**
 * web/src/components/speaker/BatchRunContext.tsx
 * Above-the-route state for the speaker batch actions (design:
 * docs/design_handoff_speaker_batch). One provider, mounted in AppShell, owns:
 *   - the batch DRAWER's select/confirm config (which action + a snapshot of the
 *     candidate speakers the roster handed over), and
 *   - the single live RUN (a background card-generation / logistics-release
 *     fan-out), polled for progress so the dock survives navigation and reload.
 *
 * Living above the <Outlet /> is what lets the run dock persist while the user
 * moves between speaker pages, and re-hydrate (via listActiveBatchRuns) after a
 * reload — the design's core fix for "no clue it's still in progress." The
 * drawer + dock read this context; the roster is the only writer of the select
 * config (openDrawer), and the drawer is the only caller of startRun.
 *
 * No send path: a card run opens the illustration gate, a logistics run opens
 * the logistics gate for review. Nothing here approves or emails.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEvent } from '../../hooks/useEvent';
import { useAuth } from '../../hooks/useAuth';
import { clearDraft, key as draftKey } from '../../lib/forms/draft';
import { useFlag } from '../../lib/flags';
import { FLAGS } from '../../lib/flags';
import { qk } from '../../lib/queryKeys';
import { toastApiError } from '../../lib/api';
import {
  getBatchRun,
  listActiveBatchRuns,
  retryBatchRun,
  startBatchRun,
  type BatchRun,
  type BatchRunAction,
  type BatchRunSeed,
} from '../../api/speakerBatch';

/** One speaker the drawer can include, as the roster snapshots it at open time. */
export interface DrawerCandidate {
  itemId: string;
  name: string;
  org: string;
  eligible: boolean;
  /** Why an ineligible speaker was left out (shown in the "not eligible" list). */
  skipReason?: string | null;
}

interface BatchRunContextValue {
  eventId: string | null;
  // --- Drawer (select/confirm) config --------------------------------------
  drawerOpen: boolean;
  action: BatchRunAction | null;
  candidates: DrawerCandidate[];
  seedIds: string[] | null;
  /** Increments on each openDrawer — key the select view off it to reset state. */
  openNonce: number;
  /** Open the drawer in SELECT mode for an action, with a candidate snapshot. */
  openDrawer: (action: BatchRunAction, candidates: DrawerCandidate[], seedIds?: string[]) => void;
  closeDrawer: () => void;
  // --- Live run -------------------------------------------------------------
  run: BatchRun | null;
  starting: boolean;
  retrying: boolean;
  /** Start a run for the current drawer action (logistics passes its stepId). */
  startRun: (seeds: BatchRunSeed[], stepId?: string) => Promise<void>;
  /** Start a run for an explicit action outside the drawer (Speaker Approvals'
   * bulk Regenerate). Progress shows in the dock; the drawer stays closed.
   * Resolves true when the run started. */
  startBackgroundRun: (action: BatchRunAction, seeds: BatchRunSeed[]) => Promise<boolean>;
  retry: () => Promise<void>;
  /** Reopen the drawer onto the live run (the dock's click target). */
  reopenRun: () => void;
  /** Forget the current run (dock disappears) — used after handoff / dismiss. */
  dismiss: () => void;
}

const BatchRunContext = createContext<BatchRunContextValue | null>(null);

const POLL_MS = 1200;

export function BatchRunProvider({ children }: { children: ReactNode }) {
  const { eventId } = useEvent();
  const { me } = useAuth();
  const speakerFlag = useFlag(FLAGS.speakerOnboarding);
  const qc = useQueryClient();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [action, setAction] = useState<BatchRunAction | null>(null);
  const [candidates, setCandidates] = useState<DrawerCandidate[]>([]);
  const [seedIds, setSeedIds] = useState<string[] | null>(null);
  const [openNonce, setOpenNonce] = useState(0);
  const [jobId, setJobId] = useState<string | null>(null);
  // jobIds the user has dismissed — so a still-cached rehydrate list can't
  // re-adopt one the moment its jobId is cleared.
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => new Set());
  const [starting, setStarting] = useState(false);
  const [retrying, setRetrying] = useState(false);

  // Poll the tracked run while it is running; stop once it settles.
  const runQuery = useQuery({
    queryKey: ['speakerBatchRun', jobId],
    queryFn: () => getBatchRun(jobId as string),
    enabled: jobId != null,
    refetchInterval: (q) => (q.state.data?.state === 'running' ? POLL_MS : false),
  });
  const run = runQuery.data ?? null;

  // Re-hydrate on mount / navigation: adopt the newest still-running run when we
  // aren't already tracking one, so the dock reappears after a reload. Adopted
  // during render (guarded so it fires once, disabling the query) — the
  // codebase's setState-from-render pattern, not an effect.
  const rehydrate = useQuery({
    queryKey: ['speakerActiveBatchRuns', eventId],
    queryFn: () => listActiveBatchRuns(eventId as string),
    enabled: speakerFlag && eventId != null && jobId == null,
    staleTime: 0,
  });
  const adoptable = rehydrate.data?.[0];
  if (jobId == null && adoptable != null && !dismissedIds.has(adoptable.jobId)) {
    setJobId(adoptable.jobId);
    setAction(adoptable.action);
  }

  // When a run finishes, re-derive server-computed readiness (new gates, roster).
  useEffect(() => {
    if (run?.state === 'done') {
      // A regenerated gate's server copy is fresh — drop any locally stashed
      // draft of it, or the stale local copy would win when the gate reopens
      // (F14; the inline RegenerateDialog path does the same per gate).
      if (run.action === 'regenerate' && me?.id) {
        for (const it of run.items) {
          if (it.status === 'done' && it.workflowId != null && it.stepIndex != null) {
            clearDraft(draftKey(it.workflowId, it.stepIndex, me.id));
          }
        }
      }
      qc.invalidateQueries({ queryKey: qk.speakers.root() });
      qc.invalidateQueries({ queryKey: qk.gates.root() });
      qc.invalidateQueries({ queryKey: qk.workflows.root() });
    }
  }, [run?.state, run?.jobId, run?.action, run?.items, me?.id, qc]);

  const openDrawer = useCallback(
    (a: BatchRunAction, cands: DrawerCandidate[], seed?: string[]) => {
      setAction(a);
      setCandidates(cands);
      setSeedIds(seed ?? null);
      setOpenNonce((n) => n + 1);
      setDrawerOpen(true);
    },
    [],
  );

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const reopenRun = useCallback(() => {
    if (run) setAction(run.action);
    setDrawerOpen(true);
  }, [run]);

  const dismiss = useCallback(() => {
    setDismissedIds((prev) => {
      if (jobId == null) return prev;
      const next = new Set(prev);
      next.add(jobId);
      return next;
    });
    setJobId(null);
    setDrawerOpen(false);
    setCandidates([]);
    setSeedIds(null);
  }, [jobId]);

  const startRun = useCallback(
    async (seeds: BatchRunSeed[], stepId?: string) => {
      if (eventId == null || action == null || seeds.length === 0) return;
      setStarting(true);
      try {
        const started = await startBatchRun(action, eventId, seeds, stepId);
        qc.setQueryData(['speakerBatchRun', started.jobId], started); // seed cache → instant progress view
        setJobId(started.jobId);
        setDrawerOpen(true);
      } catch (err) {
        toastApiError(err, 'Could not start the batch');
      } finally {
        setStarting(false);
      }
    },
    [eventId, action, qc],
  );

  const startBackgroundRun = useCallback(
    async (a: BatchRunAction, seeds: BatchRunSeed[]): Promise<boolean> => {
      if (eventId == null || seeds.length === 0) return false;
      setStarting(true);
      try {
        const started = await startBatchRun(a, eventId, seeds);
        qc.setQueryData(['speakerBatchRun', started.jobId], started); // instant dock progress
        setAction(a);
        setJobId(started.jobId);
        setDrawerOpen(false);
        return true;
      } catch (err) {
        toastApiError(err, 'Could not start the batch');
        return false;
      } finally {
        setStarting(false);
      }
    },
    [eventId, qc],
  );

  const retry = useCallback(async () => {
    if (jobId == null) return;
    setRetrying(true);
    try {
      const next = await retryBatchRun(jobId);
      qc.setQueryData(['speakerBatchRun', jobId], next);
      void runQuery.refetch(); // resume polling — state is running again
    } catch (err) {
      toastApiError(err, 'Could not retry the batch');
    } finally {
      setRetrying(false);
    }
  }, [jobId, qc, runQuery]);

  const value = useMemo<BatchRunContextValue>(
    () => ({
      eventId: eventId ?? null,
      drawerOpen,
      action,
      candidates,
      seedIds,
      openNonce,
      openDrawer,
      closeDrawer,
      run,
      starting,
      retrying,
      startRun,
      startBackgroundRun,
      retry,
      reopenRun,
      dismiss,
    }),
    [
      eventId,
      drawerOpen,
      action,
      candidates,
      seedIds,
      openNonce,
      openDrawer,
      closeDrawer,
      run,
      starting,
      retrying,
      startRun,
      startBackgroundRun,
      retry,
      reopenRun,
      dismiss,
    ],
  );

  return <BatchRunContext.Provider value={value}>{children}</BatchRunContext.Provider>;
}

/** Access the batch-run context. Throws outside <BatchRunProvider>. */
export function useBatchRun(): BatchRunContextValue {
  const ctx = useContext(BatchRunContext);
  if (ctx == null) throw new Error('useBatchRun must be used within <BatchRunProvider>');
  return ctx;
}

/** Like {@link useBatchRun} but null outside <BatchRunProvider> (for shared
 * components that only opt into background runs when a provider exists). */
export function useOptionalBatchRun(): BatchRunContextValue | null {
  return useContext(BatchRunContext);
}
